import { v4 as uuid } from "uuid";
import { HeaderPositionType, TextStyleType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

const WEEKDAY_MATCHERS = [
    { regex: /\bsun(day)?\b/i, index: 0 },
    { regex: /\bmon(day)?\b/i, index: 1 },
    { regex: /\btue(s(day)?)?\b/i, index: 2 },
    { regex: /\bwed(nesday)?\b/i, index: 3 },
    { regex: /\bthu(r(s(day)?)?)?\b/i, index: 4 },
    { regex: /\bfri(day)?\b/i, index: 5 },
    { regex: /\bsat(urday)?\b/i, index: 6 },
];
const EXPLICIT_YEAR_REGEX = /\b(19|20)\d{2}\b/;

export class CalendarCategory extends BaseCategory {
    get name() {
        return "Calendar";
    }

    get type() {
        return SlideCategories.CALENDAR;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use when the user asks for a monthly overview, week view, monthly calendar, weekly calendar, schedule by date, or a list of dated events to place on a calendar.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Parse the userinput into calendar events.
                For each event, include title, optional description, optional time, and date if available.
                If the userinput explicitly asks for a week view or month view, set preferredCalendarType accordingly. Otherwise use "none".`;
    }

    getImagePrompt() {
        return false;
    }

    getPromptSchema(options = {}) {
        return {
            events: [{
                date: "an optional date, day of week, or time period for the event",
                title: "a short event title",
                description: "an optional short description for the event",
                time: "an optional time such as 9:00 AM",
            }],
            preferredCalendarType: "none | month | week",
            ...this.getConclusionSchema(options)
        };
    }

    validateGeneratedModel(model) {
        return this.normalizeEvents(model).length > 0;
    }

    getSlideModels(model, options = {}) {
        const events = this.normalizeEvents(model);
        const dateParsingContext = this.buildDateParsingContext({
            ...model,
            events,
        });

        const normalizedModel = {
            ...model,
            events,
            dateParsingContext,
        };

        const monthModel = this.buildMonthCalendar(normalizedModel, options);
        const weekModel = this.buildWeekCalendar(normalizedModel, options);

        if (normalizedModel.preferredCalendarType === "week") {
            return [weekModel, monthModel];
        }
        return [monthModel, weekModel];
    }

    buildDateParsingContext(model) {
        const events = Array.isArray(model?.events) ? model.events : [];
        const yearSources = [
            model?.slideTitle,
            model?.slideSubTitle,
            model?.userPrompt,
            model?.prompt,
            ...events.map(event => event?.date),
        ];

        for (const source of yearSources) {
            const year = this.extractExplicitYear(source);
            if (year != null) {
                return { inferredYear: year };
            }
        }

        return { inferredYear: new Date().getFullYear() };
    }

    normalizeEvents(model) {
        const rawEvents = model?.events ?? model?.items;
        if (!this.validateArray(rawEvents)) {
            return [];
        }

        return rawEvents.map(event => ({
            date: event?.date ?? event?.day ?? "",
            title: event?.title ?? event?.name ?? "",
            description: event?.description ?? "",
            time: event?.time ?? "",
        })).filter(event => event.title || event.description || event.date || event.time);
    }

    createEmptyDays() {
        return new Array(31).fill(null).map((_, day) => ({
            day,
            text: "",
            label: {},
            items: [],
        }));
    }

    buildMonthCalendar(model, options = {}) {
        const builder = new SlideModelBuilder("calendar-month", model, model.preferredCalendarType === "month");
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        const events = model.events ?? [];
        const baseDate = this.getBaseDate(events, model.dateParsingContext);
        const monthStart = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
        const daysInMonth = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0).getDate();

        const eventsByDay = new Array(daysInMonth).fill(null).map(() => []);
        let nextFallbackDay = 0;

        for (const event of events) {
            let dayIndex = this.getMonthDayIndex(event.date, monthStart, model.dateParsingContext);
            if (dayIndex == null) {
                dayIndex = nextFallbackDay % daysInMonth;
                nextFallbackDay++;
            }
            eventsByDay[dayIndex].push(event);
        }

        const days = this.createEmptyDays();
        for (let dayIndex = 0; dayIndex < daysInMonth; dayIndex++) {
            const blocks = eventsByDay[dayIndex]
                .map(event => this.createEventBlocks(builder, event))
                .flat()
                .filter(block => block);

            if (blocks.length > 0) {
                days[dayIndex].task = {
                    text: blocks[0]?.html ?? "",
                    blocks,
                };
            }
        }

        builder.createPrimary({
            showTitle: false,
            showWeekends: true,
            startOnMonday: false,
            calendarType: "month",
            date: this.toIsoDate(monthStart),
            days,
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }
        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        return builder.model;
    }

    buildWeekCalendar(model, options = {}) {
        const builder = new SlideModelBuilder("calendar-week", model, model.preferredCalendarType === "week");
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        const events = model.events ?? [];
        const baseDate = this.getBaseDate(events, model.dateParsingContext);
        const weekStart = this.getStartOfWeek(baseDate);

        const eventsByDay = new Array(7).fill(null).map(() => []);
        let nextFallbackDay = 0;

        for (const event of events) {
            let dayIndex = this.getWeekDayIndex(event.date, model.dateParsingContext);
            if (dayIndex == null) {
                dayIndex = nextFallbackDay % 7;
                nextFallbackDay++;
            }
            eventsByDay[dayIndex].push(event);
        }

        const days = this.createEmptyDays();
        for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
            days[dayIndex].items = eventsByDay[dayIndex].map(event => ({
                id: uuid(),
                text: {
                    blocks: this.createEventBlocks(builder, event),
                }
            })).filter(item => item.text.blocks.length > 0);
        }

        builder.createPrimary({
            showTitle: false,
            showWeekends: true,
            startOnMonday: false,
            calendarType: "week",
            date: this.toIsoDate(weekStart),
            days,
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }
        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        return builder.model;
    }

    createEventBlocks(builder, event) {
        const title = this.formatEventTitle(event);
        const blocks = [
            builder.createBlock(title, TextStyleType.TITLE),
            builder.createBlock(event.description, TextStyleType.BODY),
        ].filter(block => block);

        if (blocks.length === 0) {
            const fallbackText = (event.date || "").trim();
            const fallbackBlock = builder.createBlock(fallbackText, TextStyleType.BODY);
            if (fallbackBlock) {
                blocks.push(fallbackBlock);
            }
        }

        return blocks;
    }

    formatEventTitle(event) {
        const title = (event.title || "").trim();
        const time = (event.time || "").trim();

        if (time && title) {
            return `${time}: ${title}`;
        }
        return title || time;
    }

    getBaseDate(events, dateParsingContext) {
        for (const event of events) {
            const parsedDate = this.parseDate(event.date, dateParsingContext);
            if (parsedDate) {
                return parsedDate;
            }
        }
        return new Date();
    }

    getMonthDayIndex(dateInput, monthStart, dateParsingContext) {
        const parsedDate = this.parseDate(dateInput, dateParsingContext);
        if (!parsedDate) {
            return null;
        }
        if (parsedDate.getFullYear() !== monthStart.getFullYear() || parsedDate.getMonth() !== monthStart.getMonth()) {
            return null;
        }
        return parsedDate.getDate() - 1;
    }

    getWeekDayIndex(dateInput, dateParsingContext) {
        const parsedDate = this.parseDate(dateInput, dateParsingContext);
        if (parsedDate) {
            return parsedDate.getDay();
        }
        return this.parseWeekdayIndex(dateInput);
    }

    parseDate(dateInput, dateParsingContext = {}) {
        if (typeof dateInput !== "string" || !dateInput.trim()) {
            return null;
        }

        const candidates = this.getDateParseCandidates(dateInput);
        for (const candidate of candidates) {
            const parsedDate = this.parseDateCandidate(candidate, dateParsingContext.inferredYear);
            if (parsedDate) {
                return parsedDate;
            }
        }

        return null;
    }

    parseDateCandidate(candidate, inferredYear) {
        if (typeof candidate !== "string" || !candidate.trim()) {
            return null;
        }

        const trimmed = candidate.trim();
        const hasExplicitYear = EXPLICIT_YEAR_REGEX.test(trimmed);
        const explicitParse = new Date(trimmed);
        if (!Number.isNaN(explicitParse.getTime()) && hasExplicitYear) {
            return explicitParse;
        }

        const normalizedWithYear = this.attachYearToDateString(trimmed, inferredYear);
        if (!normalizedWithYear) {
            return null;
        }

        const inferredParse = new Date(normalizedWithYear);
        if (Number.isNaN(inferredParse.getTime())) {
            return null;
        }
        return inferredParse;
    }

    attachYearToDateString(text, inferredYear) {
        const year = Number.isInteger(inferredYear) ? inferredYear : new Date().getFullYear();
        const trimmed = `${text}`.trim();

        const monthDayMatch = trimmed.match(/^([A-Za-z]{3,9})\s+(\d{1,2})$/);
        if (monthDayMatch) {
            const [, month, day] = monthDayMatch;
            return `${month} ${day}, ${year}`;
        }

        const numericMonthDayMatch = trimmed.match(/^(\d{1,2})[/-](\d{1,2})$/);
        if (numericMonthDayMatch) {
            const [, month, day] = numericMonthDayMatch;
            return `${month}/${day}/${year}`;
        }

        if (EXPLICIT_YEAR_REGEX.test(trimmed)) {
            return trimmed;
        }

        return null;
    }

    extractExplicitYear(text) {
        if (typeof text !== "string") {
            return null;
        }
        const match = text.match(EXPLICIT_YEAR_REGEX);
        if (!match) {
            return null;
        }
        return Number.parseInt(match[0], 10);
    }

    getDateParseCandidates(dateInput) {
        const trimmed = `${dateInput}`.trim();
        const candidates = [trimmed];

        const colonIndex = trimmed.indexOf(":");
        if (colonIndex > 0) {
            candidates.push(trimmed.slice(0, colonIndex).trim());
        }

        const leadingDateMatch = trimmed.match(/^((?:sun|mon|tue|wed|thu|fri|sat)(?:day)?[,]?\s+)?([A-Za-z]{3,9}\s+\d{1,2},\s+\d{4}|\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[/-]\d{1,2}[/-]\d{2,4})\b/i);
        if (leadingDateMatch?.[0]) {
            candidates.push(leadingDateMatch[0].trim());
        }

        return [...new Set(candidates)].filter(Boolean);
    }

    parseWeekdayIndex(text) {
        if (typeof text !== "string") {
            return null;
        }

        for (const matcher of WEEKDAY_MATCHERS) {
            if (matcher.regex.test(text)) {
                return matcher.index;
            }
        }

        return null;
    }

    getStartOfWeek(date) {
        const weekStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        return weekStart;
    }

    toIsoDate(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }
}
